import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Landmark, Download, AlertCircle } from "lucide-react";
import { api, errMsg, money, fmtDate, label } from "../lib/api";
import { Avatar, Select, Spinner } from "./bits";

export function PayoutRunPanel({ group, tick, onDone }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState([]);
  const [fmt, setFmt] = useState("generic");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/groups/${group.id}/payout-runs/ready`).then((r) => { setD(r.data); setSel([]); }).catch(() => setD({ payouts: [], runs: [], formats: ["generic"] }));
  useEffect(() => { load(); }, [group.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Spinner />;
  const ready = d.payouts.filter((p) => p.destination_ready);
  const total = d.payouts.filter((p) => sel.includes(p.id)).reduce((a, p) => a + p.amount, 0);
  const fileUrl = (run, format) => `${process.env.REACT_APP_BACKEND_URL}/api/payout-runs/${run.id}/file?format=${format}&auth=${localStorage.getItem("nini_token")}`;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/payout-runs`, { payout_ids: sel, bank_format: fmt });
      toast.success(t("payout.success"));
      window.open(fileUrl(data, "csv"), "_blank");
      load(); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-5 md:p-6 space-y-4" data-testid="payout-run-panel">
      <div className="flex items-start gap-3"><Landmark size={20} className="text-nini-deep mt-0.5" /><div><h3 className="font-semibold text-nini-heading">{t("payout.runTitle")}</h3><p className="text-sm text-nini-muted mt-1">{t("payout.runHint")}</p></div></div>
      {d.payouts.length === 0 ? <p className="text-sm text-nini-muted" data-testid="payout-run-empty">{t("payout.noReady")}</p> : (
        <div className="divide-y divide-nini-border">
          {d.payouts.map((p) => (
            <label key={p.id} className={`flex items-center gap-3 py-3 ${p.destination_ready ? "cursor-pointer" : "opacity-70"}`} data-testid={`payout-run-row-${p.id}`}>
              <input type="checkbox" className="h-4 w-4 accent-nini-green" disabled={!p.destination_ready} checked={sel.includes(p.id)} onChange={(e) => setSel(e.target.checked ? [...sel, p.id] : sel.filter((x) => x !== p.id))} data-testid={`payout-run-check-${p.id}`} />
              <Avatar src={p.recipient?.avatar_url} name={p.recipient?.name} size="h-9 w-9" />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium text-nini-heading truncate">{p.recipient?.name} <span className="text-nini-muted font-normal">· {p.reason}</span></div>
                {p.destination_ready ? <div className="text-xs text-nini-muted">{p.bank.bank_name} · {p.bank.account_number_masked} · {label(p.bank.account_type || "")}</div>
                  : <div className="text-xs text-[#B47D00] inline-flex items-center gap-1" title={t("payout.askBank")}><AlertCircle size={12} /> {t("payout.noBank")}</div>}
              </div>
              <span className="text-sm font-semibold text-nini-heading">{money(p.amount)}</span>
            </label>
          ))}
        </div>
      )}
      {d.payouts.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button className="text-sm text-nini-deep font-medium" data-testid="payout-run-select-all" onClick={() => setSel(ready.map((p) => p.id))}>{t("payout.selectAll")}</button>
          <Select testId="payout-run-format" value={fmt} onChange={setFmt} options={d.formats.map((f) => [f, f === "generic" ? "Generic CSV" : label(f)])} className="!w-44 !h-10" />
          <button className="btn-primary ml-auto" data-testid="payout-run-submit" disabled={busy || sel.length === 0} onClick={run}><Download size={16} /> {t("payout.payOut", { count: sel.length, total: money(total) })}</button>
        </div>
      )}
      {d.runs.length > 0 && (
        <div className="pt-2"><div className="eyebrow mb-2">{t("payout.recent")}</div>
          {d.runs.map((r) => <div key={r.id} className="flex items-center gap-3 py-2 text-sm border-t border-nini-border" data-testid={`payout-run-${r.id}`}><span className="flex-1 text-nini-text">{fmtDate(r.created_at, true)} · {r.count} · {money(r.total)} · {label(r.bank_format)} · {r.created_by_name}</span>
            <a className="text-nini-deep font-medium" href={fileUrl(r, "csv")} target="_blank" rel="noreferrer" data-testid={`payout-run-csv-${r.id}`}>{t("payout.csv")}</a><a className="text-nini-deep font-medium" href={fileUrl(r, "xlsx")} target="_blank" rel="noreferrer">{t("payout.xlsx")}</a></div>)}
        </div>
      )}
    </div>
  );
}
